import find from 'lodash/find'
import { get } from 'lodash'
import { default as deserialize, formatMeta } from 'app/utils/deserialize'
import { fetch } from 'app/utils/data'
import { NON_OFFERED_PLANS } from 'app/views/plans/PlansData'

const INITIAL_STATE = Object.freeze({
  list: [],
  item: null,
  subscriptionDiscounts: [],
  affiliateMeta: null,
  addOnPrices: [],
  purchaseMessage: ''
})

export default {
  namespaced: true,

  state: { ...INITIAL_STATE },

  getters: {
    // "currentPlans" means "plans we currently offer for new purchases."
    // Legacy plans stay `active: true` in the DB because existing customers
    // are still on them, so the active flag is no longer the right gate.
    // The authoritative "what's offered" list lives in PlansData.js
    // (`NON_OFFERED_PLANS`); anything not on that deny-list is offered.
    currentPlans: (state) => state.list.filter((plan) => !NON_OFFERED_PLANS.includes(plan.sku)),

    plansLoaded: (state) => state.list.length > 0,

    allPlans: (state) => state.list,

    currentPlan: (state) => state.item,

    getPlan: (state) => (sku) => {
      return find(state.list, { sku: sku })
    },

    getPlanBySlug: (state) => (slug) => {
      return find(state.list, { slug: slug })
    },

    affiliateMeta: (state) => state.affiliateMeta,

    addOnPrices: (state) => state.addOnPrices,

    discount: (state) => state.discount,

    subscriptionDiscounts: (state) => state.subscriptionDiscounts,

    purchaseMessage: (state) => state.purchaseMessage
  },

  mutations: {
    plansFetched(state, { plans, affiliateMeta, addOnPrices = [] }) {
      state.list = plans
      state.affiliateMeta = affiliateMeta
      state.addOnPrices = addOnPrices
    },

    planFetched(state, { plan }) {
      state.item = plan
    },

    discountsFetched(state, { discounts }) {
      state.subscriptionDiscounts = discounts
    },

    setPurchaseMessage(state, message) {
      state.purchaseMessage = message
    },

    resetState(state) {
      state = { ...INITIAL_STATE }
    }
  },

  actions: {
    async fetchPlans({ commit }) {
      const response = await fetch({ url: `/api/plans` })

      const plans = await deserialize(response.data)
      const affiliateMeta = formatMeta(get(response.data, ['meta', 'affiliate']))
      const addOnPrices = get(response.data, ['meta', 'add_ons'], []).map(formatMeta)

      commit('plansFetched', { plans, affiliateMeta, addOnPrices })

      return plans
    },

    // Download-gate callers need the plan list to tell a cycle block from
    // a Cloud block; the guard keeps them from re-fetching what a plan view
    // already loaded.
    async ensurePlansLoaded({ dispatch, getters }) {
      if (getters.plansLoaded) return

      await dispatch('fetchPlans')
    },

    async fetchPlan({ commit }, sku) {
      const response = await fetch({ url: `/api/plans/${sku}` })

      const plan = await deserialize(response.data)

      commit('planFetched', { plan })

      return plan
    },

    async fetchDiscounts({ commit }, couponIds) {
      try {
        const response = await fetch({ url: `/api/discounts`, params: { couponIds: couponIds } })

        const discounts = await deserialize(response.data)

        commit('discountsFetched', { discounts: discounts })

        return discounts
      } catch (error) {
        return error
      }
    }
  }
}
